/**
 * Confirmed Automation Calculation Service
 * NO HARDCODED VALUES OR FALLBACKS ALLOWED
 * Only calculates two metrics:
 * 1. Client Confirmed Automation (using client percentages from the allocation screen)
 * 2. Benchmark Confirmed Automation (using database benchmark percentages)
 */

interface TaskData {
  taskName: string
  pillar: string
  ftesImpacted: number
  automationPercent: number
  benchmarkAutomation?: number
}

interface AutomationBenchmark {
  id: string
  task_label: string
  pillar: string
  benchmark_percent: number
}

export class CalculationService {
  private static automationBenchmarks: AutomationBenchmark[] = []
  private static initialized = false
  private static initializingPromise: Promise<void> | null = null

  /**
   * Initialize calculation service by loading automation benchmarks
   */
  static async initialize(benchmarks?: AutomationBenchmark[]) {
    // If benchmarks are provided, use them and mark as initialized
    if (benchmarks && benchmarks.length > 0) {
      this.automationBenchmarks = benchmarks
      this.initialized = true
      this.initializingPromise = null
      return
    }

    if (this.initialized) return
    if (this.initializingPromise) {
      return this.initializingPromise
    }

    try {
      this.initializingPromise = (async () => {
        const response = await fetch('/api/automation-benchmarks')
        if (response.ok) {
          const result = await response.json()
          // Normalize API response shape: support array or { benchmarks }
          this.automationBenchmarks = Array.isArray(result) ? result : (result.benchmarks || [])
          // console.log('🧮 CalculationService initialized with benchmarks:', this.automationBenchmarks.length)
        } else {
          throw new Error('Failed to load automation benchmarks')
        }
        this.initialized = true
      })()

      await this.initializingPromise
    } catch (error) {
      console.error('❌ Failed to initialize CalculationService:', error)
      throw error
    }
  }

  /**
   * Calculate Client Confirmed Automation
   * Uses automation percentages set by client in allocation screen
   * Weighted by FTEs allocated to each task
   */
  static calculateClientWeightedAutomationPotential(taskData: TaskData[]): number {
    if (!taskData || taskData.length === 0) {
      console.warn('⚠️ No task data provided for client weighted automation calculation')
      return 0
    }

    const totalFTEs = taskData.reduce((sum, task) => sum + task.ftesImpacted, 0)
    if (totalFTEs === 0) {
      console.warn('⚠️ Total FTEs is zero for client weighted automation calculation')
      return 0
    }

    const weightedSum = taskData.reduce((sum, task) => {
      const fteWeight = task.ftesImpacted / totalFTEs
      const weightedAutomation = fteWeight * task.automationPercent
      // console.log(`📊 Task: ${task.taskName}, FTEs: ${task.ftesImpacted}, Weight: ${fteWeight.toFixed(3)}, Automation: ${task.automationPercent}%, Weighted: ${weightedAutomation.toFixed(2)}`)
      return sum + weightedAutomation
    }, 0)

    // console.log('✅ Client Confirmed Automation:', result + '%')
    return Math.round(weightedSum)
  }

  /**
   * Calculate Benchmark Confirmed Automation
   * Uses benchmark percentages from database for each task/activity
   * Weighted by FTEs allocated to each task
   */
  static calculateBenchmarkWeightedAutomationPotential(taskData: TaskData[]): number {
    if (!taskData || taskData.length === 0) {
      console.warn('⚠️ No task data provided for benchmark weighted automation calculation')
      return 0
    }

    // Enrich task data with benchmark automation percentages
    const enrichedTaskData = taskData.map(task => {
      const benchmark = this.findBenchmarkForTask(task)
      const benchmarkPercent = benchmark?.benchmark_percent || 0
      // console.log(`🎯 Task: ${task.taskName}, Benchmark: ${benchmarkPercent}% (${benchmark ? 'found' : 'not found'})`)
      return {
        ...task,
        benchmarkAutomation: benchmarkPercent
      }
    })

    const totalFTEs = enrichedTaskData.reduce((sum, task) => sum + task.ftesImpacted, 0)
    if (totalFTEs === 0) {
      console.warn('⚠️ Total FTEs is zero for benchmark weighted automation calculation')
      return 0
    }

    const weightedSum = enrichedTaskData.reduce((sum, task) => {
      const fteWeight = task.ftesImpacted / totalFTEs
      const weightedAutomation = fteWeight * task.benchmarkAutomation
      // console.log(`📊 Task: ${task.taskName}, FTEs: ${task.ftesImpacted}, Weight: ${fteWeight.toFixed(3)}, Benchmark: ${task.benchmarkAutomation}%, Weighted: ${weightedAutomation.toFixed(2)}`)
      return sum + weightedAutomation
    }, 0)

    // console.log('✅ Benchmark Confirmed Automation:', result + '%')
    return Math.round(weightedSum)
  }

  /**
   * Find matching benchmark for a task
   */
  private static findBenchmarkForTask(task: TaskData): AutomationBenchmark | undefined {
    // Try the exact task name match first
    let benchmark = this.automationBenchmarks.find(b => 
      b.task_label.toLowerCase() === task.taskName.toLowerCase()
    )

    // If no exact match, try pillar-based matching
    if (!benchmark) {
      benchmark = this.automationBenchmarks.find(b => {
        const pillarName = task.pillar.toLowerCase()
        const benchmarkPillar = b.pillar?.toLowerCase() || ''
        
        return (
          benchmarkPillar === pillarName ||
          (pillarName.includes('account') && benchmarkPillar.includes('account')) ||
          (pillarName.includes('journal') && benchmarkPillar.includes('journal')) ||
          (pillarName.includes('intercompany') && benchmarkPillar.includes('intercompany')) ||
          (pillarName.includes('close') && benchmarkPillar.includes('close'))
        )
      })
    }

    return benchmark
  }
}

export default CalculationService
